import type { RuntimeContext } from "./types.js";

const RUNTIME_VARIABLE_DEPENDENCY_PREFIX = "__runtimeVariable:";

export function dependencyForRuntimeVariable(variableId: string): string {
  return `${RUNTIME_VARIABLE_DEPENDENCY_PREFIX}${variableId}`;
}

export function parseRuntimeVariableDependency(
  dependency: string,
): string | null {
  return dependency.startsWith(RUNTIME_VARIABLE_DEPENDENCY_PREFIX)
    ? dependency.slice(RUNTIME_VARIABLE_DEPENDENCY_PREFIX.length)
    : null;
}

export function readRuntimeVariable(ctx: RuntimeContext, variableId: string) {
  const state = ctx.__runtimeVariableStateById;
  return state?.has(variableId) ? state.get(variableId) : undefined;
}

export function hasRuntimeVariable(
  ctx: RuntimeContext,
  variableId: string,
): boolean {
  return ctx.__runtimeVariableStateById?.has(variableId) === true;
}

export function writeRuntimeVariable(
  ctx: RuntimeContext,
  variableId: string,
  value: unknown,
) {
  let state = ctx.__runtimeVariableStateById;
  if (!state) {
    state = new Map<string, unknown>();
    ctx.__runtimeVariableStateById = state;
  }

  let versions = ctx.__runtimeVariableVersionById;
  if (!versions) {
    versions = new Map<string, number>();
    ctx.__runtimeVariableVersionById = versions;
  }

  state.set(variableId, value);
  versions.set(variableId, (versions.get(variableId) ?? 0) + 1);
  // A flow can read the same expression before and after a variable write.
  // The holder is shared by nested scopes; discard all pre-write results.
  if (ctx.__expressionCache) {
    ctx.__expressionCache.byContext = new WeakMap();
    ctx.__expressionCache.byScope = new WeakMap();
  }
}

export function readRuntimeVariableVersion(
  ctx: RuntimeContext,
  variableId: string,
): number {
  return ctx.__runtimeVariableVersionById?.get(variableId) ?? 0;
}

export function buildRuntimeVariableVersionSignature(
  versions: Map<string, number>,
): string {
  if (versions.size === 0) {
    return "";
  }

  return [...versions.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([variableId, version]) => `${variableId}:${version}`)
    .join("|");
}
